import { Box, Icon } from '@chakra-ui/react';
import { OrganizationObject } from '@client/graphql/__generated__/types';
import { ChevronDownIcon } from 'lucide-react';
import { FC, memo, useMemo } from 'react';
import { DropdownIndicatorProps, GroupBase } from 'react-select';
import { chakraStyles, Select } from '~/services/document/common/Select';

interface OrganizationSelectProps {
  selectedOrganizationId?: string;
  organizations?: Pick<OrganizationObject, 'id' | 'name'>[];
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'unstyled';
  onChange: (organizationId?: string) => void;
}

type OrgOption = { label: string; value: string };

function AnimatedDropdownIndicator<
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({ selectProps }: DropdownIndicatorProps<Option, IsMulti, Group>) {
  return (
    <Box alignItems="center" color="whiteAlpha.600" display="flex">
      <Icon
        as={ChevronDownIcon}
        size={14}
        style={{
          transform: selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s ease',
        }}
      />
    </Box>
  );
}

export const OrganizationSelect: FC<OrganizationSelectProps> = memo(
  function OrganizationSelect({
    organizations,
    selectedOrganizationId,
    size = 'md',
    variant = 'default',
    onChange,
  }) {
    const selectedOrganization = useMemo(() => {
      if (!organizations?.length) {
        return undefined;
      }

      const selected = organizations?.find(
        (organization) => organization.id === selectedOrganizationId
      );

      if (selected) {
        return {
          label: selected?.name,
          value: selected?.id,
        };
      }
    }, [organizations, selectedOrganizationId]);

    const unstyledChakraStyles =
      variant === 'unstyled'
        ? {
            container: (base: object) => ({ ...base, maxWidth: '220px' }),
            control: (base: object) => ({
              ...base,
              border: 'none',
              boxShadow: 'none',
              bg: 'transparent',
              backgroundColor: 'transparent',
              display: 'inline-flex',
              _hover: { bg: 'transparent', borderColor: 'transparent' },
              _focus: { bg: 'transparent', boxShadow: 'none' },
              _focusVisible: { boxShadow: 'none', borderColor: 'transparent' },
              minHeight: 'unset',
              height: 'auto',
              cursor: 'pointer',
            }),
            singleValue: (base: object) => ({
              ...base,
              color: 'white',
              fontWeight: 'medium',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }),
            dropdownIndicator: (base: object) => ({
              ...base,
              bg: 'transparent',
              color: 'whiteAlpha.600',
              px: 0,
              pl: 1,
            }),
            indicatorSeparator: () => ({ display: 'none' }),
            valueContainer: (base: object) => ({
              ...base,
              px: 0,
              flex: '0 1 auto',
              minWidth: 0,
            }),
            menu: (base: object) => ({
              ...base,
              minWidth: '200px',
              bg: 'indigo.700',
              borderWidth: '1px',
              borderColor: 'whiteAlpha.300',
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.7)',
              borderRadius: 'md',
              mt: 1,
            }),
            menuList: (base: object) => ({
              ...base,
              bg: 'indigo.700',
              py: 1,
              borderRadius: 'md',
            }),
            option: (base: object) => ({
              ...base,
              fontSize: 'sm',
              py: 3,
              px: 4,
              color: 'white',
              bg: 'transparent',
              _hover: { bg: 'whiteAlpha.100' },
              _selected: { bg: 'whiteAlpha.200' },
              cursor: 'pointer',
            }),
          }
        : {
            ...chakraStyles,
            container: (base: object) => ({ ...base, width: '100%' }),
          };

    return (
      <Select<OrgOption>
        chakraStyles={unstyledChakraStyles}
        components={
          variant === 'unstyled'
            ? { DropdownIndicator: AnimatedDropdownIndicator }
            : undefined
        }
        id="organization-select"
        isClearable={false}
        isSearchable={false}
        menuPortalTarget={window.document.body}
        options={organizations?.map((organization) => ({
          label: organization.name,
          value: organization.id,
        }))}
        size={size}
        styles={{
          menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
          }),
        }}
        value={selectedOrganization}
        onChange={(value) => onChange(value?.value)}
      />
    );
  }
);
